import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faUser, 
  faPhone, 
  faEnvelope, 
  faExternalLinkAlt,
  faPlus,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { getInitials } from 'helpers/utils';

interface RelatedParty {
  lead_id: any;
  role: string;
  name: string;
  lead: {
    lead_first_name: string;
    lead_last_name: string;
    lead_email?: string;
    lead_phone?: string;
    id: string;
  };
}

interface DealRelatedPartiesCardProps {
  relatedParties: RelatedParty[];
  className?: string;
  dealId?: string | number;
  onAddParty?: () => void;
}

const DealRelatedPartiesCard: React.FC<DealRelatedPartiesCardProps> = ({
  relatedParties = [],
  className,
  dealId,
  onAddParty
}) => {
  const getRoleColor = (role: string) => {
    const roleColors: { [key: string]: string } = {
      'buyer': 'success',
      'seller': 'primary',
      'agent': 'info',
      'attorney': 'warning',
      'lender': 'secondary',
      'inspector': 'dark',
      'title': 'danger',
      'default': 'secondary'
    };
    
    return roleColors[role.toLowerCase()] || roleColors.default;
  };

  const getRoleIcon = (role: string) => {
    const roleIcons: { [key: string]: any } = {
      'buyer': faUser,
      'seller': faUser,
      'agent': faUser,
      'attorney': faUser,
      'lender': faUser,
      'inspector': faUser,
      'title': faUser,
      'default': faUser
    };
    
    return roleIcons[role.toLowerCase()] || roleIcons.default;
  };

  return (
    <div className={classNames('modern-related-parties-card', className)}>
      <Card className="related-parties-card border-0">
        <Card.Body className="p-4">
          {/* Header */}
          <div className="parties-header mb-4">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-3">
                <div className="header-icon-wrapper">
                  <FontAwesomeIcon icon={faUsers} className="header-icon" />
                </div>
                <div>
                  <h5 className="header-title mb-1">Related Parties</h5>
                  <p className="header-subtitle mb-0">
                    {relatedParties.length} {relatedParties.length === 1 ? 'person' : 'people'} involved
                  </p>
                </div>
              </div>
              {onAddParty && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="add-party-btn"
                  onClick={onAddParty}
                >
                  <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                  Add Party
                </Button>
              )}
            </div>
          </div>

          {/* Parties List */}
          {relatedParties.length > 0 ? (
            <div className="parties-list">
              {relatedParties.map((party, index) => {
                const fullName = `${party.lead.lead_first_name} ${party.lead.lead_last_name}`.trim();
                const initials = getInitials(party.lead.lead_first_name, party.lead.lead_last_name);
                
                return (
                  <div key={party.lead.id || index} className="party-item">
                    <div className="party-content">
                      {/* Avatar */}
                      <div className="party-avatar">
                        <span className="avatar-text">{initials}</span>
                      </div>

                      {/* Info */}
                      <div className="party-info flex-grow-1">
                        <div className="d-flex align-items-center justify-content-between mb-1">
                          <h6 className="party-name mb-0">{fullName}</h6>
                          <Badge 
                            bg={getRoleColor(party.role)}
                            className="role-badge"
                          >
                            <FontAwesomeIcon 
                              icon={getRoleIcon(party.role)} 
                              className="me-1" 
                              size="xs"
                            />
                            {party.role}
                          </Badge>
                        </div>

                        {/* Contact Info */}
                        <div className="contact-info">
                          {party.lead.lead_email && (
                            <div className="contact-item">
                              <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                              <span className="contact-text">{party.lead.lead_email}</span>
                            </div>
                          )}
                          {party.lead.lead_phone && (
                            <div className="contact-item">
                              <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                              <span className="contact-text">{party.lead.lead_phone}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="party-actions">
                        <Button
                          as={Link}
                          to={`/leads/${party.lead.id}`}
                          variant="outline-primary"
                          size="sm"
                          className="view-btn"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <h6 className="empty-title">No Related Parties</h6>
              <p className="empty-text">
                Add people involved in this deal to keep track of all stakeholders.
              </p>
              {onAddParty && (
                <Button
                  variant="primary"
                  size="sm"
                  className="empty-action-btn"
                  onClick={onAddParty}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add First Party
                </Button>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .modern-related-parties-card {
          --parties-primary: #0d6efd;
          --parties-success: #198754;
          --parties-info: #0dcaf0;
          --parties-warning: #ffc107;
          --parties-secondary: #6c757d;
          --parties-light: #f8f9fa;
          --parties-dark: #212529;
          --parties-border: #e9ecef;
          --parties-shadow: 0 4px 20px rgba(0,0,0,0.08);
          --parties-shadow-hover: 0 6px 25px rgba(0,0,0,0.12);
        }

        .related-parties-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 16px !important;
          box-shadow: var(--parties-shadow);
          transition: all 0.3s ease;
          overflow: hidden;
          position: relative;
        }

        .related-parties-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--parties-primary), var(--parties-success), var(--parties-info));
        }

        .related-parties-card:hover {
          box-shadow: var(--parties-shadow-hover);
          transform: translateY(-2px);
        }

        .parties-header {
          border-bottom: 1px solid var(--parties-border);
          padding-bottom: 1rem;
        }

        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, var(--parties-primary), var(--parties-info));
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.25);
        }

        .header-icon {
          color: white;
          font-size: 1.2rem;
        }

        .header-title {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--parties-dark);
          margin: 0;
        }

        .header-subtitle {
          font-size: 0.85rem;
          color: var(--parties-secondary);
          margin: 0;
        }

        .add-party-btn {
          border-radius: 10px !important;
          font-weight: 500 !important;
          font-size: 0.85rem !important;
          padding: 0.5rem 1rem !important;
          transition: all 0.2s ease !important;
        }

        .add-party-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.25);
        }

        .parties-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .party-item {
          background: white;
          border: 1px solid var(--parties-border);
          border-radius: 12px;
          padding: 1rem;
          transition: all 0.2s ease;
        }

        .party-item:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transform: translateY(-1px);
          border-color: var(--parties-primary);
        }

        .party-content {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }

        .party-avatar {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, var(--parties-secondary), var(--parties-dark));
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }

        .avatar-text {
          color: white;
          font-size: 1rem;
          font-weight: 600;
          text-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }

        .party-info {
          min-width: 0;
        }

        .party-name {
          font-size: 1rem;
          font-weight: 600;
          color: var(--parties-dark);
          margin: 0;
        }

        .role-badge {
          display: flex !important;
          align-items: center;
          padding: 0.4rem 0.75rem !important;
          border-radius: 8px !important;
          font-weight: 500 !important;
          font-size: 0.75rem !important;
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          margin-top: 0.5rem;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .contact-icon {
          color: var(--parties-secondary);
          font-size: 0.8rem;
          width: 12px;
        }

        .contact-text {
          font-size: 0.85rem;
          color: var(--parties-secondary);
        }

        .party-actions {
          flex-shrink: 0;
        }

        .view-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px !important;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 !important;
          transition: all 0.2s ease !important;
        }

        .view-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(13, 110, 253, 0.25);
        }

        .empty-state {
          text-align: center;
          padding: 2rem 1rem;
        }

        .empty-icon {
          width: 64px;
          height: 64px;
          background: var(--parties-light);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
          font-size: 1.5rem;
          color: var(--parties-secondary);
        }

        .empty-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--parties-dark);
          margin-bottom: 0.5rem;
        }

        .empty-text {
          font-size: 0.9rem;
          color: var(--parties-secondary);
          margin-bottom: 1.5rem;
          line-height: 1.4;
        }

        .empty-action-btn {
          background: linear-gradient(135deg, var(--parties-primary), var(--parties-info)) !important;
          border: none !important;
          border-radius: 10px !important;
          padding: 0.6rem 1.2rem !important;
          font-weight: 500 !important;
          transition: all 0.2s ease !important;
        }

        .empty-action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(13, 110, 253, 0.3);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .party-content {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
          }

          .party-avatar {
            width: 40px;
            height: 40px;
            align-self: flex-start;
          }

          .avatar-text {
            font-size: 0.9rem;
          }

          .contact-info {
            margin-top: 0.25rem;
          }

          .party-actions {
            align-self: flex-end;
          }
        }

        /* Animation for new items */
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .party-item {
          animation: slideInUp 0.3s ease forwards;
        }
        `
      }} />
    </div>
  );
};

export default DealRelatedPartiesCard;